import { FC, useMemo } from 'react';
import {
  CtaFeatureSlice,
  CtaFeatureSliceCenteredButton,
  CtaFeatureSliceDefaultItem,
  CtaFeatureSliceImageWithLogoAndButton,
  CtaFeatureSliceImageWithTitleAndButton,
  CtaFeatureSliceImageWithTwoCta,
  CtaFeatureSliceTitleWithCta,
  CtaFeatureSliceTitleWithTwoLinks,
} from '@/types';
import { Container } from '@/components';
import classNames from 'classnames';
import { RenderCta } from './CtaFeature.components';
import { getSlicePadding, isCurrentDateInRange } from '@/utils';
import { useSelectedStore } from '@/hooks';
import { format } from 'date-fns';

import styles from './CtaFeature.module.scss';

const CtaFeature: FC<CtaFeatureSlice> = slice => {
  const { store: currentStore } = useSelectedStore();
  const background = slice?.primary?.background_color ?? '';
  const borderTop = slice?.primary?.border_section ?? '';
  const borderBottom = slice?.primary?.border_section ?? '';
  const paddingSection = slice?.primary?.padding_section;
  const fullWidth =
    slice?.variation === 'default' ? slice?.primary?.cta_full_width : false;

  const imageTwoCTAalignLeft =
    slice.variation === 'imageWithTwoCta' &&
    slice?.primary?.content_align === 'left';

  const shouldHideSlice = useMemo(() => {
    if (slice?.variation === 'default') {
      const items = slice?.items?.filter(
        filterDefaultCTAItemsByStoreAndDate(currentStore?.url_slug),
      );

      return !items?.length;
    }

    return !isSliceActiveForStore(currentStore?.url_slug ?? '', slice);
  }, [slice, currentStore]);

  if (shouldHideSlice || !slice?.primary?.active) {
    return null;
  }

  return (
    <section
      className={classNames(
        styles.cta_section,
        {
          [styles.cta_section__spacing_bottom]: slice?.primary?.bottom_spacing,
        },
        getSlicePadding(paddingSection),
      )}
      style={{ background, borderTop, borderBottom }}
    >
      <Container
        className={classNames({
          [styles.full_width_container]:
            fullWidth || slice.variation === 'imageWithLogoAndButton',
          [styles.centered_button_variation_container]:
            slice.variation === 'centeredButton',
          [styles.default_variation_container]: slice.variation === 'default',
          [styles.image_two_cta_variation_container]:
            slice.variation === 'imageWithTwoCta',
          [styles.image_two_cta_variation_container__align_left]:
            imageTwoCTAalignLeft,
          [styles.title_cta_variation_container]:
            slice.variation === 'titleWithCta',
          [styles.title_two_link_variation_container]:
            slice.variation === 'titleWithTwoLinks',
          [styles.image_title_button_variation_container]:
            slice.variation === 'imageWithTitleAndButton',
          [styles.image_logo_button_variation_container]:
            slice.variation === 'imageWithLogoAndButton',
        })}
      >
        <RenderCta {...slice} />
      </Container>
    </section>
  );
};

const filterDefaultCTAItemsByStoreAndDate =
  (currentStoreUrl?: string) => (item: CtaFeatureSliceDefaultItem) => {
    const { location_slug: slug, start_date: start, end_date: end } = item;
    const currentDay = format(new Date(), 'eeee');
    const isItemDayValid = [null, 'Everyday', currentDay].includes(item?.day);
    const isItemDateInRange = isCurrentDateInRange(start, end);
    const isItemLocationValid = [null, currentStoreUrl].includes(slug);

    return isItemDayValid && isItemDateInRange && isItemLocationValid;
  };

const isSliceActiveForStore = (
  currentStoreUrl: string,
  slice:
    | CtaFeatureSliceImageWithLogoAndButton
    | CtaFeatureSliceImageWithTitleAndButton
    | CtaFeatureSliceImageWithTwoCta
    | CtaFeatureSliceTitleWithCta
    | CtaFeatureSliceTitleWithTwoLinks
    | CtaFeatureSliceCenteredButton,
) => {
  const {
    location_slug: slug,
    start_date: start,
    end_date: end,
    day,
  } = slice.primary;
  const currentDay = format(new Date(), 'eeee');
  const isItemDayValid = [null, 'Everyday', currentDay]?.includes(day);
  const isItemDateInRange = isCurrentDateInRange(start, end);
  const isItemLocationValid = [null, currentStoreUrl]?.includes(slug);

  return isItemDayValid && isItemDateInRange && isItemLocationValid;
};

export default CtaFeature;
